export type LinkPreview = {
  thumbnailUrl: string;
  title?: string;
};

export function isSafeHttpUrl(href: string): boolean {
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function isDirectImageUrl(href: string): boolean {
  if (!isSafeHttpUrl(href)) {
    return false;
  }
  const path = new URL(href).pathname.toLowerCase();
  return /\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(path);
}

export function getYouTubeVideoId(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.hostname === 'youtu.be') {
      const id = url.pathname.slice(1).split('/')[0];
      return id || null;
    }
    if (
      url.hostname.includes('youtube.com') ||
      url.hostname.includes('youtube-nocookie.com')
    ) {
      const fromQuery = url.searchParams.get('v');
      if (fromQuery) {
        return fromQuery;
      }
      const fromPath = url.pathname.match(/^\/(embed|shorts|live)\/([^/?]+)/);
      return fromPath?.[2] ?? null;
    }
  } catch {
    return null;
  }
  return null;
}

export function getYouTubeThumbnailUrl(href: string): string | null {
  const id = getYouTubeVideoId(href);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
}

const NOEMBED_FETCH_TIMEOUT_MS = 3000;

type NoembedResponse = {
  thumbnail_url?: string | null;
  title?: string | null;
};

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

export async function fetchLinkPreviewFromNoembed(
  href: string,
  signal?: AbortSignal
): Promise<LinkPreview | null> {
  const timeoutSignal = AbortSignal.timeout(NOEMBED_FETCH_TIMEOUT_MS);
  const fetchSignal = signal
    ? AbortSignal.any([signal, timeoutSignal])
    : timeoutSignal;

  try {
    const res = await fetch(
      `https://noembed.com/embed?url=${encodeURIComponent(href)}`,
      { signal: fetchSignal }
    );
    if (!res.ok) {
      return null;
    }
    const data = (await res.json()) as NoembedResponse;
    if (!data.thumbnail_url) {
      return null;
    }
    return {
      thumbnailUrl: data.thumbnail_url,
      title: data.title?.trim() || undefined,
    };
  } catch (error) {
    if (isAbortError(error)) {
      return null;
    }
    console.error('Failed to fetch link preview thumbnail:', error);
    return null;
  }
}

export async function resolveLinkPreview(
  href: string,
  signal?: AbortSignal
): Promise<LinkPreview | null> {
  try {
    if (!isSafeHttpUrl(href)) {
      return null;
    }
    if (isDirectImageUrl(href)) {
      return { thumbnailUrl: href };
    }
    const youtubeThumbnail = getYouTubeThumbnailUrl(href);
    if (youtubeThumbnail) {
      return { thumbnailUrl: youtubeThumbnail };
    }
    return await fetchLinkPreviewFromNoembed(href, signal);
  } catch (error) {
    if (isAbortError(error)) {
      return null;
    }
    console.error('Failed to resolve link preview:', error);
    return null;
  }
}

const MARKDOWN_LINK_PATTERN = /\[([^\]]*)\]\(([^)]+)\)/g;
const MARKDOWN_AUTOLINK_PATTERN = /<((https?:\/\/[^>\s]+))>/gi;

/**
 * Collect unique http(s) link targets from markdown (excludes image syntax).
 */
export function extractMarkdownLinkHrefs(markdown: string): string[] {
  const hrefs = new Set<string>();

  for (const match of markdown.matchAll(MARKDOWN_LINK_PATTERN)) {
    const index = match.index ?? 0;
    if (index > 0 && markdown[index - 1] === '!') {
      continue;
    }
    const href = match[2]?.trim();
    if (href && isSafeHttpUrl(href)) {
      hrefs.add(href);
    }
  }

  for (const match of markdown.matchAll(MARKDOWN_AUTOLINK_PATTERN)) {
    const href = match[1]?.trim();
    if (href && isSafeHttpUrl(href)) {
      hrefs.add(href);
    }
  }

  return [...hrefs];
}

/** Resolve previews for many hrefs in parallel (one batch per caller). */
export async function resolveLinkPreviews(
  hrefs: readonly string[],
  signal?: AbortSignal
): Promise<Map<string, LinkPreview | null>> {
  const unique = [...new Set(hrefs)];
  const entries = await Promise.all(
    unique.map(async href => {
      const preview = await resolveLinkPreview(href, signal);
      return [href, preview] as const;
    })
  );
  return new Map(entries);
}

export function formatLinkHostname(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return href;
  }
}
